/**
 * Sentence slots — what the automations centre's builder needs to render a recipe
 * (`specs/M6-A2-automations-center.md` §4, captures `discovery/ui-reference/automations/`).
 *
 * monday's editor is a sentence with clickable placeholders. In the DOM a placeholder is
 * `.ds-text-component-content-text.fs-mask` and a fixed word is
 * `.recipe-builder-node-sentence-part.term-text` (INDEX.md), and **a slot with a visible
 * default still counts as unconfigured**: "Create automation" stays inert until every
 * placeholder has been chosen explicitly.
 *
 * A block already carries the sentence (`sentencePartial`, `{label, paramKey}`); what it does
 * not carry is *which picker* each placeholder opens, because monday's own catalogue keeps
 * that in `configurationFields`, which we did not import. The table below is that mapping, in
 * one place: by parameter name, with per-block overrides for the three names monday reuses
 * for different pickers (`columnValue` is a status label on `1838` and a person on `50`/`1810`).
 * A unit test walks every registered block and fails on an unmapped placeholder, so a new
 * block cannot silently render a slot nobody can fill (CLAUDE.md rule 6).
 */
import { automationBlockRegistry } from './block.js';
import { isAnyValue } from './values.js';
/** Which picker a placeholder opens. Every one of these is a capture in the INDEX. */
export const SLOT_KINDS = [
    /** `13-slot-status-picker` / `35-date-column-picker`: the board's columns + subitem columns. */
    'column',
    /** `51-status-value-picker`: the label list, first entry "Anything" (`$any$`). */
    'status_label',
    /** `78324`: a *set* of dropdown labels. */
    'dropdown_labels',
    /** `contains one of` / `contains only` / `contains all`. */
    'dropdown_condition',
    /** `is` / `is not`. */
    'is_condition',
    /** `24-group-picker`, including "Top group (currently '…')". */
    'group',
    /** `36-receiver-picker`: Me · Team member · People (column) · Subscribers · Team · Guest. */
    'receiver',
    /** A person from the account (block `50`, `1810`). */
    'person',
    /** `21..23-scheduler-*`: Daily/Weekly/Monthly/Yearly + weekday chips + "Every N …, at hh:mm". */
    'recurrence',
    /** `31..34-datetrigger-*`: "when date arrives at {time}" / "{N} {unit} {before|after}". */
    'date_trigger',
    /** "…by {some days}" — a count and a unit. */
    'duration',
    /** `39-message-editor`: free text with `{item's name}`-style tokens. */
    'message',
    /** A plain single-line string (a subitem name). */
    'text',
    /** `assign` / `add assignee`. */
    'assign_behavior',
    /** `507`: the new item's group and column values. */
    'item_values',
    /** `ez.a.set_column`: a value whose editor follows the chosen column's type. */
    'value',
    /* ── M6-A4 (`specs/M6-A4-integrations.md` §4) ──────────────────────────── */
    /** A board of the account — the cross-board creators' `targetBoardId` / `linkageBoards`. */
    'board',
    /** monday's `dynamicMapping` editor: target field ← literal text + source fields. */
    'item_mapping',
    /** The same editor with Google Calendar's five fields instead of a board's columns. */
    'event_mapping',
    /** `88520`: template, name, workspace/folder, owners. */
    'board_mapping',
    /** The Slack channel list of the account's connection (`remote: {app:'slack', type:'channels'}`). */
    'slack_channel',
    /** The Slack user list (`remote: {app:'slack', type:'users'}`). */
    'slack_user',
    /** Which stored connection the node uses — read-only until a second one exists. */
    'connection',
    /** Recipe `174`'s `email` slot: a subject line and a body, both token-bearing. */
    'email',
    /** The generic webhook's destination URL, plus its optional signing secret. */
    'webhook_url',
];
/** Parameter name → picker. The default; overridden per block below where monday reuses a name. */
const BY_PARAM = {
    columnId: { kind: 'column' },
    dateColumnId: { kind: 'column', columnTypes: ['date', 'timeline'] },
    dropdownColumnId: { kind: 'column', columnTypes: ['dropdown'] },
    statusColumnValue: { kind: 'status_label' },
    previousStatusColumnValue: { kind: 'status_label' },
    desiredStatusValue: { kind: 'status_label' },
    desiredPrevStatusValue: { kind: 'status_label' },
    desiredNewStatusValue: { kind: 'status_label' },
    dropdownColumnValues: { kind: 'dropdown_labels' },
    dropdownColumnValuesCondition: { kind: 'dropdown_condition' },
    isCondition: { kind: 'is_condition' },
    groupId: { kind: 'group' },
    schedulerConfig: { kind: 'recurrence' },
    dateTriggerConfig: { kind: 'date_trigger' },
    duration: { kind: 'duration' },
    message: { kind: 'message' },
    updateBody: { kind: 'message' },
    receiver: { kind: 'receiver' },
    assignBehavior: { kind: 'assign_behavior' },
    itemColumnValues: { kind: 'item_values' },
    name: { kind: 'text' },
    value: { kind: 'value' },
    columnValue: { kind: 'status_label' },
    // M6-A4
    targetBoardId: { kind: 'board' },
    linkageBoards: { kind: 'board' },
    linkageColumn: { kind: 'column', columnTypes: ['board-relation'] },
    itemMapping: { kind: 'item_mapping' },
    eventMapping: { kind: 'event_mapping' },
    boardMapping: { kind: 'board_mapping' },
    slackChannel: { kind: 'slack_channel' },
    slackUser: { kind: 'slack_user' },
    connection: { kind: 'connection' },
    email: { kind: 'email' },
    url: { kind: 'webhook_url' },
};
/** `<blockId>.<param>` wins over the name alone. */
const BY_BLOCK_PARAM = {
    '50.columnValue': { kind: 'person' },
    '1810.columnValue': { kind: 'person' },
};
export function slotFor(blockId, param) {
    return BY_BLOCK_PARAM[`${blockId}.${param}`] ?? BY_PARAM[param];
}
const PLACEHOLDER = /\{([^{},]*),\s*([A-Za-z0-9_]+)\}/g;
/**
 * Splits a block's sentence into fixed words and placeholders, in order. The label is monday's
 * own grey default text ("something", "group", "someone"), shown until the slot is filled.
 */
export function parseSentence(block) {
    const parts = [];
    let cursor = 0;
    for (const match of block.sentencePartial.matchAll(PLACEHOLDER)) {
        const [whole, label = '', param = ''] = match;
        const at = match.index;
        if (at > cursor)
            parts.push({ kind: 'text', text: block.sentencePartial.slice(cursor, at) });
        parts.push({ kind: 'slot', label: label.trim(), param, slot: slotFor(block.id, param) ?? { kind: 'value' } });
        cursor = at + whole.length;
    }
    if (cursor < block.sentencePartial.length)
        parts.push({ kind: 'text', text: block.sentencePartial.slice(cursor) });
    return parts;
}
/** Every placeholder of a block, in sentence order. */
export function slotsOf(block) {
    return parseSentence(block)
        .filter((part) => part.kind === 'slot')
        .map(({ label, param, slot }) => ({ label, param, slot }));
}
/**
 * Is this parameter chosen? monday's rule, restated: an *absent* value is unconfigured, and so
 * is an empty list or an empty string. `$any$` ("Anything") **is** a choice, and so is `0`.
 */
export function isSlotFilled(value) {
    if (value === undefined || value === null)
        return false;
    if (typeof value === 'string')
        return value.trim().length > 0;
    if (Array.isArray(value))
        return value.length > 0;
    if (isAnyValue(value))
        return true;
    if (typeof value === 'object')
        return Object.keys(value).length > 0;
    return true;
}
/**
 * The gate behind "Create automation" (capture 20/25/37/40: the button is inert until every
 * placeholder — `isCondition` included — has been chosen).
 */
export function recipeCompleteness(recipe) {
    const missing = [];
    const nodes = [recipe.trigger, ...recipe.conditions, ...recipe.actions];
    for (const node of nodes) {
        const block = automationBlockRegistry.find(node.block);
        if (block === undefined) {
            missing.push({ nodeId: node.nodeId, block: node.block, param: '', label: node.block });
            continue;
        }
        for (const { param, label } of slotsOf(block)) {
            if (!isSlotFilled(node.params[param]))
                missing.push({ nodeId: node.nodeId, block: node.block, param, label });
        }
    }
    return { complete: missing.length === 0, missing };
}
/**
 * The sentence a card shows (capture 15/63: "1 day before **Date** arrives notify **Roberto**").
 * `format` renders one filled slot; an unfilled one keeps monday's grey default label.
 */
export function renderRecipeSentence(recipe, format = () => null) {
    const nodes = [recipe.trigger, ...recipe.conditions, ...recipe.actions];
    return nodes
        .map((node) => {
        const block = automationBlockRegistry.find(node.block);
        if (block === undefined)
            return node.block;
        return parseSentence(block)
            .map((part) => {
            if (part.kind === 'text')
                return part.text;
            const value = node.params[part.param];
            if (!isSlotFilled(value))
                return part.label;
            return format({ blockId: node.block, param: part.param, value, label: part.label }) ?? part.label;
        })
            .join('');
    })
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}
//# sourceMappingURL=slots.js.map